import React, { useState } from 'react';
import { CalendarIcon, PlusIcon, ChevronDownIcon } from 'lucide-react';
import TrendChart from './TrendChart';
type FilterId = 'all' | 'home' | 'away' | 'win' | 'loss';
type TrendMetric = {
  id: string;
  title: string;
  ticks: number[];
  decimals: number;
  base: number[];
};
const filters: {
  id: FilterId;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'home',
  label: 'Home'
}, {
  id: 'away',
  label: 'Away'
}, {
  id: 'win',
  label: 'Win'
}, {
  id: 'loss',
  label: 'Loss'
}];

// Multiplier applied to the base series so each filter shows a coherent split
const filterFactor: Record<FilterId, number> = {
  all: 1,
  home: 1.08,
  away: 0.92,
  win: 1.14,
  loss: 0.84
};
const dates = ['02/07/2026', '09/07/2026', '16/07/2026', '23/07/2026', '30/07/2026', '06/08/2026'];
const metricCatalog: TrendMetric[] = [{
  id: '2p_pct',
  title: 'Two points percent (2P%)',
  ticks: [0, 20, 40, 60, 80],
  decimals: 2,
  base: [58.4, 60.1, 59.6, 62.3, 61.8, 64.2]
}, {
  id: '3pm',
  title: 'Three points made (3PM)',
  ticks: [0, 0.5, 1, 1.5, 2],
  decimals: 2,
  base: [1.0, 1.2, 1.35, 1.5, 1.7, 2.0]
}, {
  id: 'pts',
  title: 'Points per game (PTS)',
  ticks: [0, 10, 20, 30],
  decimals: 2,
  base: [18.4, 20.1, 22.6, 19.8, 24.2, 23.5]
}, {
  id: 'ast',
  title: 'Assists per game (AST)',
  ticks: [0, 2, 4, 6, 8],
  decimals: 2,
  base: [3.2, 4.1, 3.8, 5.2, 4.9, 5.6]
}, {
  id: 'reb',
  title: 'Rebounds per game (REB)',
  ticks: [0, 3, 6, 9, 12],
  decimals: 2,
  base: [7.1, 6.4, 8.2, 7.8, 9.1, 8.6]
}, {
  id: 'ft_pct',
  title: 'Free throw percent (FT%)',
  ticks: [0, 25, 50, 75, 100],
  decimals: 2,
  base: [74.5, 78.2, 76.9, 81.4, 79.8, 84.1]
}];
const seasons = ['WorldCup Qualification Europe for Men', 'NBA Regular Season 2025/26', 'NBA Playoffs 2024/25'];
const PlayerTrends = () => {
  const [activeFilter, setActiveFilter] = useState<FilterId>('all');
  const [season, setSeason] = useState(seasons[0]);
  const [activeMetricIds, setActiveMetricIds] = useState<string[]>(['2p_pct', '3pm']);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const activeMetrics = activeMetricIds.map(id => metricCatalog.find(m => m.id === id)).filter((m): m is TrendMetric => Boolean(m));
  const availableMetrics = metricCatalog.filter(m => !activeMetricIds.includes(m.id));
  const scaleSeries = (metric: TrendMetric) => {
    const max = Math.max(...metric.ticks);
    return metric.base.map(v => Math.min(max, Number((v * filterFactor[activeFilter]).toFixed(2))));
  };
  const removeMetric = (id: string) => setActiveMetricIds(ids => ids.filter(i => i !== id));
  const addMetric = (id: string) => {
    setActiveMetricIds(ids => [...ids, id]);
    setIsPickerOpen(false);
  };
  return <section className="space-y-4" aria-label="Player trends" data-id="element-978" data-mp="hqc06xaa6el8">
      {/* Controls */}
      <div className="bg-white border rounded-lg shadow-sm px-4 py-3 flex flex-wrap items-center justify-between gap-4" data-id="element-979" data-mp="3u8bxlg90gzik">
        <div className="flex flex-wrap items-center gap-4" data-id="element-980" data-mp="80vmd60dynku">
          <div className="flex items-center bg-gray-100 rounded-md p-0.5" role="group" aria-label="Match filter" data-id="element-981" data-mp="82gi2yyfscka">
            {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} aria-pressed={activeFilter === filter.id} className={`px-3 py-1 text-xs font-bold rounded transition-colors ${activeFilter === filter.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-982" data-mp="gzz70ef7bb1g">
                {filter.label}
              </button>)}
          </div>

          <button className="flex items-center gap-2 px-3 py-1.5 border rounded-md text-xs font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors" data-id="element-983" data-mp="vqpvbchl3rzt">
            <CalendarIcon className="w-4 h-4" data-id="element-984" data-mp="2p7cfm11kny87" />
            Select Period
          </button>
        </div>

        <div className="flex flex-col" data-id="element-985" data-mp="2u5x8tgiephn5">
          <label htmlFor="trend-season" className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1" data-id="element-986" data-mp="2z01dtu843fll">
            Select Season
          </label>
          <div className="relative" data-id="element-987" data-mp="26bclajl4gilg">
            <select id="trend-season" value={season} onChange={e => setSeason(e.target.value)} className="appearance-none bg-transparent border-b border-gray-300 focus:border-gray-900 focus:outline-none pr-6 pb-1 text-sm font-medium text-gray-900" data-id="element-988" data-mp="26c5m73xegdvo">
              {seasons.map(s => <option key={s} value={s} data-id="element-989" data-mp="3roffcjyesu1w">
                  {s}
                </option>)}
            </select>
            <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-990" data-mp="3vno6dh22edtr" />
          </div>
        </div>
      </div>

      {/* Charts */}
      {activeMetrics.length > 0 ? <div className="space-y-4" data-id="element-991" data-mp="1g2obmy4r7yt2">
          {activeMetrics.map(metric => <TrendChart key={metric.id} title={metric.title} dates={dates} values={scaleSeries(metric)} ticks={metric.ticks} decimals={metric.decimals} onRemove={() => removeMetric(metric.id)} data-id="element-992" data-mp="1b7r7a4if6dmi" />)}
        </div> : <div className="bg-white border rounded-lg shadow-sm py-12 text-center" data-id="element-993" data-mp="sag9ypgclu9p">
          <p className="text-sm text-gray-600" data-id="element-994" data-mp="nbms6ku13h3q">No trends selected.</p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-995" data-mp="ndhea2h1df9e">
            Add a trend to start comparing performance over time.
          </p>
        </div>}

      {/* Add trend */}
      <div className="flex flex-col items-center" data-id="element-996" data-mp="2hqz5q0kkaw9p">
        <button onClick={() => setIsPickerOpen(open => !open)} disabled={availableMetrics.length === 0} aria-expanded={isPickerOpen} className="flex items-center gap-2 px-4 py-2 rounded-md border text-xs font-bold text-gray-900 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-997" data-mp="30uars6135ngt">
          <PlusIcon className="w-4 h-4" data-id="element-998" data-mp="2wql3lhobp7wb" />
          Add Trend
        </button>

        {isPickerOpen && availableMetrics.length > 0 && <div className="mt-2 w-full max-w-sm bg-white border rounded-lg shadow-sm divide-y" data-id="element-999" data-mp="3okdf68u22593">
            {availableMetrics.map(metric => <button key={metric.id} onClick={() => addMetric(metric.id)} className="w-full text-left px-4 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors" data-id="element-1000" data-mp="3ajkfzast6lzn">
                {metric.title}
              </button>)}
          </div>}
      </div>
    </section>;
};
export default PlayerTrends;